import { motion } from 'framer-motion'
import {
  SwatchIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { Card } from '../components/ui/Card'

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
}

const features = [
  { title: 'Logo design', desc: 'Unique, memorable logos that capture your brand essence.' },
  { title: 'Brand identity', desc: 'Complete visual identity systems including color palettes and typography.' },
  { title: 'Style guides', desc: 'Comprehensive brand guidelines for consistent application.' },
  { title: 'Marketing collateral', desc: 'Business cards, letterheads, social media kits, and more.' },
]

const providers = [
  { name: 'Canva', url: 'https://www.canva.com', desc: 'Design platform for everyone' },
  { name: '99designs', url: 'https://99designs.com', desc: 'Logo & brand design contests' },
  { name: 'Looka', url: 'https://looka.com', desc: 'AI-powered logo maker' },
  { name: 'Figma', url: 'https://www.figma.com', desc: 'Collaborative design tool' },
]

export const BrandingPage = () => {
  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
      <motion.div variants={item}>
        <h1 className="text-2xl font-bold text-metallic-900">Branding Services</h1>
        <p className="text-metallic-500 mt-1">Professional brand identity, logo design, and brand guidelines.</p>
      </motion.div>

      <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {features.map((f) => (
          <Card key={f.title} hover>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <SwatchIcon className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <h3 className="font-semibold text-metallic-900">{f.title}</h3>
                <p className="text-sm text-metallic-500 mt-1">{f.desc}</p>
              </div>
            </div>
          </Card>
        ))}
      </motion.div>

      <motion.div variants={item}>
        <Card>
          <h3 className="text-lg font-semibold text-metallic-900 mb-4">Service Providers</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {providers.map((p) => (
              <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between p-4 rounded-xl border border-metallic-200 hover:border-pink-300 hover:bg-pink-50 transition-all group">
                <div>
                  <p className="font-medium text-metallic-900 group-hover:text-pink-600">{p.name}</p>
                  <p className="text-sm text-metallic-500">{p.desc}</p>
                </div>
                <ArrowTopRightOnSquareIcon className="w-5 h-5 text-metallic-400 group-hover:text-pink-500" />
              </a>
            ))}
          </div>
        </Card>
      </motion.div>

      <motion.div variants={item}>
        <Card className="bg-gradient-to-r from-pink-50 to-white border-pink-200">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircleIcon className="w-6 h-6 text-pink-600" />
            <h3 className="font-semibold text-metallic-900">What's Included</h3>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {['3 concept revisions', 'Vector file formats', 'Brand color palette', 'Typography system', 'Social media kit', 'Brand style guide'].map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-metallic-600">
                <CheckCircleIcon className="w-4 h-4 text-success-500 flex-shrink-0" />{f}
              </li>
            ))}
          </ul>
        </Card>
      </motion.div>
    </motion.div>
  )
}
